/**
 * Transaction Table
 * =================
 * Interactive table for reviewing and managing transactions.
 *
 * Features:
 * - Search by vendor/amount
 * - Filters (Flagged, Unreviewed, Out-of-Range, Reclassified, Low OCR, Has Suggestions)
 * - Sortable columns
 * - Row selection
 * - Bulk operations
 * - Click to view detail
 * - Running totals footer
 * - Pattern-based reclassification suggestions
 * - Subcategory aggregation
 * - Mobile responsive
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, ArrowUp, ArrowDown, Flag, CheckSquare, AlertCircle, RotateCw, FileText, Lightbulb } from 'lucide-react';
import api from '../../utils/api';
import { PatternSuggestionBadge, SuggestionsSummary } from './PatternSuggestion';

interface PatternSuggestion {
  suggestion_id: string;
  pattern_id: string;
  transaction_id: string;
  report_id: string;
  match_score: number;
  matched_keywords: string[];
  current_category: string;
  current_subcategory?: string;
  suggested_category: string;
  suggested_subcategory: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
}

interface Transaction {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  source_file: string;
  source_page: number;
  category: string;
  subcategory?: string;
  original_category?: string;
  is_reclassified: boolean;
  checked: boolean;
  flagged: boolean;
  flag_reason?: string;
  needs_review: boolean;
  ocr_confidence?: number;
}

interface TransactionTableProps {
  transactions: Transaction[];
  expectedAmount: number;
  reportId: string;
  eventId?: string;  // Event ID for pattern suggestions (e.g., "smitty_supply_2025")
  onTransactionClick: (transaction: Transaction) => void;
  onTransactionsUpdate: () => void;
  onViewSourceDocument?: (sourceFile: string, sourcePage: number, transaction: Transaction) => void;
}

type SortField = 'date' | 'vendor' | 'amount';
type SortOrder = 'asc' | 'desc';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

export default function TransactionTable({
  transactions,
  expectedAmount,
  reportId,
  eventId,  // Optional event ID for pattern suggestions
  onTransactionClick,
  onTransactionsUpdate,
  onViewSourceDocument
}: TransactionTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Amount range filter state
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  
  // Subcategory aggregation state (P2 feature)
  const [groupBySubcategory, setGroupBySubcategory] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Pattern suggestions state
  const [suggestions, setSuggestions] = useState<Record<string, PatternSuggestion[]>>({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);

  // Fetch pattern suggestions for transactions
  const fetchSuggestions = useCallback(async () => {
    if (!reportId || !eventId || transactions.length === 0) return;
    
    setSuggestionsLoading(true);
    try {
      const response = await api.get(`/api/v1/patterns/${eventId}/suggestions/${reportId}`);
      setSuggestions(response.suggestions || {});
    } catch (error) {
      console.error('Error fetching pattern suggestions:', error);
      // Don't show error to user - suggestions are optional enhancement
    } finally {
      setSuggestionsLoading(false);
    }
  }, [reportId, eventId, transactions.length]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Calculate suggestion stats
  const suggestionStats = useMemo(() => {
    const allSuggestions = Object.values(suggestions).flat();
    return {
      total: allSuggestions.length,
      transactionsWithSuggestions: Object.keys(suggestions).length,
      byConfidence: {
        HIGH: allSuggestions.filter(s => s.confidence === 'HIGH').length,
        MEDIUM: allSuggestions.filter(s => s.confidence === 'MEDIUM').length,
        LOW: allSuggestions.filter(s => s.confidence === 'LOW').length
      }
    };
  }, [suggestions]);

  // Handle suggestion accept/reject
  const handleSuggestionAction = useCallback(() => {
    // Refresh suggestions and transactions after action
    fetchSuggestions();
    onTransactionsUpdate();
  }, [fetchSuggestions, onTransactionsUpdate]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        t =>
          (t.vendor || '').toLowerCase().includes(query) ||
          (t.description || '').toLowerCase().includes(query) ||
          t.amount.toString().includes(query)
      );
    }

    // Apply filters
    if (activeFilters.has('flagged')) {
      filtered = filtered.filter(t => t.flagged);
    }
    if (activeFilters.has('unchecked')) {
      filtered = filtered.filter(t => !t.checked);
    }
    if (activeFilters.has('reclassified')) {
      filtered = filtered.filter(t => t.is_reclassified);
    }
    if (activeFilters.has('needs_review')) {
      filtered = filtered.filter(t => t.needs_review);
    }
    
    // Low OCR confidence filter (< 70%)
    if (activeFilters.has('low_ocr')) {
      filtered = filtered.filter(t => t.ocr_confidence != null && t.ocr_confidence < 0.7);
    }

    // Has suggestions filter
    if (activeFilters.has('has_suggestions')) {
      filtered = filtered.filter(t => suggestions[t.id] && suggestions[t.id].length > 0);
    }

    // Apply amount range filter
    if (minAmount) {
      const min = parseFloat(minAmount);
      if (!isNaN(min)) {
        filtered = filtered.filter(t => Math.abs(t.amount) >= min);
      }
    }
    if (maxAmount) {
      const max = parseFloat(maxAmount);
      if (!isNaN(max)) {
        filtered = filtered.filter(t => Math.abs(t.amount) <= max);
      }
    }

    return filtered;
  }, [transactions, searchQuery, activeFilters, minAmount, maxAmount, suggestions]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'vendor':
          comparison = (a.vendor || a.description || '').localeCompare(b.vendor || b.description || '');
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredTransactions, sortField, sortOrder]);

  // Subcategory grouping (P2 feature)
  interface SubcategoryGroup {
    subcategory: string;
    transactions: Transaction[];
    totalAmount: number;
    count: number;
    avgOcrConfidence: number | null;
    hasLowConfidence: boolean;
  }

  const groupedTransactions = useMemo((): SubcategoryGroup[] => {
    if (!groupBySubcategory) return [];
    
    const groups: { [key: string]: Transaction[] } = {};
    sortedTransactions.forEach(txn => {
      const key = txn.subcategory || txn.category || 'Uncategorized';
      if (!groups[key]) groups[key] = [];
      groups[key].push(txn);
    });
    
    return Object.entries(groups).map(([subcategory, txns]) => {
      const ocrValues = txns.filter(t => t.ocr_confidence != null).map(t => t.ocr_confidence!);
      return {
        subcategory,
        transactions: txns,
        totalAmount: txns.reduce((sum, t) => sum + t.amount, 0),
        count: txns.length,
        avgOcrConfidence: ocrValues.length > 0 ? ocrValues.reduce((a, b) => a + b, 0) / ocrValues.length : null,
        hasLowConfidence: ocrValues.some(v => v < 0.7)
      };
    }).sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount));
  }, [sortedTransactions, groupBySubcategory]);

  const toggleGroupExpansion = (subcategory: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(subcategory)) {
      newExpanded.delete(subcategory);
    } else {
      newExpanded.add(subcategory);
    }
    setExpandedGroups(newExpanded);
  };

  // Calculate totals
  const runningTotal = useMemo(() => {
    return filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);

  const variance = runningTotal - expectedAmount;

  const toggleFilter = (filter: string) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedTransactions.map(t => t.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const getRowClasses = (transaction: Transaction): string => {
    const classes = ['border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors'];

    // Priority order for row coloring (most important first)
    if (transaction.flagged) {
      classes.push('border-l-4 border-yellow-500 bg-yellow-50');
    } else if (transaction.is_reclassified) {
      classes.push('border-l-4 border-blue-500 bg-blue-50');
    } else if (transaction.needs_review) {
      classes.push('bg-red-50');
    } else if (transaction.checked) {
      classes.push('bg-green-50');
    } else if (transaction.ocr_confidence != null && transaction.ocr_confidence < 0.7) {
      // Low OCR confidence - subtle orange tint
      classes.push('bg-orange-50/50 border-l-2 border-orange-300');
    } else if (transaction.ocr_confidence != null && transaction.ocr_confidence < 0.85) {
      // Medium OCR confidence - very subtle yellow tint
      classes.push('bg-amber-50/30');
    }

    return classes.join(' ');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Count flags
  const flaggedCount = transactions.filter(t => t.flagged).length;
  const uncheckedCount = transactions.filter(t => !t.checked).length;
  const reclassifiedCount = transactions.filter(t => t.is_reclassified).length;
  const needsReviewCount = transactions.filter(t => t.needs_review).length;
  const lowOcrCount = transactions.filter(t => t.ocr_confidence != null && t.ocr_confidence < 0.7).length;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Pattern Suggestions Summary Panel */}
      {suggestionStats.total > 0 && (
        <div className="p-4 border-b border-gray-200">
          <SuggestionsSummary
            transactionCount={suggestionStats.transactionsWithSuggestions}
            suggestionCount={suggestionStats.total}
            byConfidence={suggestionStats.byConfidence}
            onViewAll={() => {
              // Filter to show only transactions with suggestions
              if (!activeFilters.has('has_suggestions')) {
                toggleFilter('has_suggestions');
              }
            }}
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍 Search vendor or amount..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => toggleFilter('flagged')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              activeFilters.has('flagged')
                ? 'bg-yellow-500 text-white border-yellow-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Flag size={14} />
            Flagged
            {flaggedCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{flaggedCount}</span>
            )}
          </button>

          <button
            onClick={() => toggleFilter('unchecked')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              activeFilters.has('unchecked')
                ? 'bg-blue-500 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <CheckSquare size={14} />
            Unreviewed
            {uncheckedCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{uncheckedCount}</span>
            )}
          </button>

          <button
            onClick={() => toggleFilter('reclassified')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              activeFilters.has('reclassified')
                ? 'bg-blue-500 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <RotateCw size={14} />
            Reclassified
            {reclassifiedCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{reclassifiedCount}</span>
            )}
          </button>

          <button
            onClick={() => toggleFilter('needs_review')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              activeFilters.has('needs_review')
                ? 'bg-red-500 text-white border-red-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <AlertCircle size={14} />
            Needs Review
            {needsReviewCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{needsReviewCount}</span>
            )}
          </button>

          {/* Low OCR Confidence Filter */}
          <button
            onClick={() => toggleFilter('low_ocr')}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              activeFilters.has('low_ocr')
                ? 'bg-orange-500 text-white border-orange-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="filter-low-ocr"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            Low OCR
            {lowOcrCount > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{lowOcrCount}</span>
            )}
          </button>

          {/* Has Suggestions Filter */}
          {suggestionStats.transactionsWithSuggestions > 0 && (
            <button
              onClick={() => toggleFilter('has_suggestions')}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                activeFilters.has('has_suggestions')
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="filter-has-suggestions"
            >
              <Lightbulb size={14} />
              <span className="hidden sm:inline">Suggestions</span>
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/20">{suggestionStats.transactionsWithSuggestions}</span>
            </button>
          )}

          {/* Group by Subcategory Toggle (P2 Feature) */}
          <button
            onClick={() => {
              setGroupBySubcategory(!groupBySubcategory);
              if (!groupBySubcategory) setExpandedGroups(new Set());
            }}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              groupBySubcategory
                ? 'bg-purple-500 text-white border-purple-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="toggle-group-subcategory"
            title="Group transactions by subcategory"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
            </svg>
            <span className="hidden sm:inline">Group</span>
          </button>

          {/* Amount Range Filter - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1 ml-2 border-l border-gray-200 pl-3">
            <span className="text-xs text-gray-500 font-medium">Amount:</span>
            <span className="text-xs text-gray-400">$</span>
            <input
              type="number"
              placeholder="Min"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="w-16 lg:w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-400">–</span>
            <input
              type="number"
              placeholder="Max"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="w-16 lg:w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {(minAmount || maxAmount) && (
              <button
                onClick={() => { setMinAmount(''); setMaxAmount(''); }}
                className="text-xs text-gray-500 hover:text-gray-700 ml-1"
                title="Clear amount filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Mobile Amount Filter - Shown on mobile only */}
        <div className="md:hidden mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">Amount:</span>
          <input
            type="number"
            placeholder="Min"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
          <span className="text-xs text-gray-400">–</span>
          <input
            type="number"
            placeholder="Max"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
        </div>

        <div className="text-sm text-gray-600 mt-3 flex flex-wrap items-center gap-2">
          <span>
            Showing <span className="font-semibold">{sortedTransactions.length}</span> of{' '}
            <span className="font-semibold">{transactions.length}</span> transactions
          </span>
          {groupBySubcategory && (
            <span className="text-purple-600 font-medium">
              • {groupedTransactions.length} subcategories
            </span>
          )}
        </div>
      </div>

      {/* Grouped View (P2 Feature) */}
      {groupBySubcategory ? (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <div className="divide-y divide-gray-200">
            {groupedTransactions.map(group => (
              <div key={group.subcategory} className="bg-white">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroupExpansion(group.subcategory)}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    group.hasLowConfidence ? 'bg-orange-50/50' : ''
                  }`}
                  data-testid={`group-header-${group.subcategory}`}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${expandedGroups.has(group.subcategory) ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="font-medium text-gray-900">{group.subcategory}</span>
                    <span className="text-sm text-gray-500">({group.count} transactions)</span>
                    {group.hasLowConfidence && (
                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">Low OCR</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {group.avgOcrConfidence != null && (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        group.avgOcrConfidence >= 0.9 ? 'bg-green-100 text-green-700' :
                        group.avgOcrConfidence >= 0.7 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        Avg: {(group.avgOcrConfidence * 100).toFixed(0)}%
                      </span>
                    )}
                    <span className={`font-semibold ${group.totalAmount >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                      {formatCurrency(group.totalAmount)}
                    </span>
                  </div>
                </button>
                
                {/* Expanded Transactions */}
                {expandedGroups.has(group.subcategory) && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    <table className="w-full">
                      <tbody>
                        {group.transactions.map(transaction => (
                          <tr
                            key={transaction.id}
                            className={getRowClasses(transaction)}
                            onClick={() => onTransactionClick(transaction)}
                          >
                            <td className="pl-12 pr-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                              {new Date(transaction.date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {transaction.vendor || transaction.description || '—'}
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-medium">
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {transaction.ocr_confidence != null && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  transaction.ocr_confidence >= 0.9 ? 'bg-green-100 text-green-700' :
                                  transaction.ocr_confidence >= 0.7 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {(transaction.ocr_confidence * 100).toFixed(0)}%
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              {suggestions[transaction.id] && suggestions[transaction.id].length > 0 && (
                                <PatternSuggestionBadge
                                  suggestion={suggestions[transaction.id][0]}
                                  onAccept={handleSuggestionAction}
                                  onReject={handleSuggestionAction}
                                  compact={true}
                                />
                              )}
                            </td>
                            <td className="px-4 py-2 text-center text-lg">
                              {transaction.flagged ? '🚩' : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {groupedTransactions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No transactions match your filters</p>
            </div>
          )}
        </div>
      ) : (
      /* Standard Table View */
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              <th className="px-2 sm:px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sortedTransactions.length && sortedTransactions.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th
                onClick={() => handleSort('date')}
                className="px-2 sm:px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  Date
                  <SortIcon field="date" />
                </div>
              </th>
              <th
                onClick={() => handleSort('vendor')}
                className="px-2 sm:px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-1">
                  <span className="hidden sm:inline">Vendor / Description</span>
                  <span className="sm:hidden">Vendor</span>
                  <SortIcon field="vendor" />
                </div>
              </th>
              <th
                onClick={() => handleSort('amount')}
                className="px-2 sm:px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center justify-end gap-1">
                  Amount
                  <SortIcon field="amount" />
                </div>
              </th>
              <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Category
              </th>
              <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Source
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                Page
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-28" title="OCR Confidence - Lower values may indicate extraction errors">
                OCR
              </th>
              <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24" title="Pattern-based reclassification suggestions">
                Suggest
              </th>
              <th className="px-2 sm:px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-12 sm:w-16">
                Flag
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedTransactions.map(transaction => (
              <tr key={transaction.id} className={getRowClasses(transaction)}>
                <td
                  className="px-2 sm:px-4 py-3"
                  onClick={e => {
                    e.stopPropagation();
                    toggleSelect(transaction.id);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(transaction.id)}
                    onChange={() => {}}
                    className="rounded border-gray-300"
                  />
                </td>
                <td
                  className="px-2 sm:px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                  onClick={() => onTransactionClick(transaction)}
                >
                  {new Date(transaction.date).toLocaleDateString()}
                </td>
                <td className="px-2 sm:px-4 py-3 text-sm text-gray-900" onClick={() => onTransactionClick(transaction)}>
                  <div>
                    <span className="line-clamp-1 font-medium">{transaction.vendor || transaction.description || '—'}</span>
                    {transaction.vendor && transaction.description && transaction.description !== transaction.vendor && (
                      <span className="line-clamp-1 text-xs text-gray-500 block">{transaction.description}</span>
                    )}
                    {transaction.flagged && transaction.flag_reason && (
                      <div className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded mt-1 hidden sm:block">
                        {transaction.flag_reason}
                      </div>
                    )}
                  </div>
                </td>
                <td
                  className="px-2 sm:px-4 py-3 text-sm text-right font-semibold text-gray-900"
                  onClick={() => onTransactionClick(transaction)}
                >
                  {formatCurrency(transaction.amount)}
                </td>
                <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-900" onClick={() => onTransactionClick(transaction)}>
                  {transaction.is_reclassified ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
                      {transaction.subcategory || transaction.category}
                    </span>
                  ) : (
                    <span className="text-gray-700">{transaction.subcategory || transaction.category}</span>
                  )}
                </td>
                <td className="hidden md:table-cell px-4 py-3 text-sm">
                  {transaction.source_file ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onViewSourceDocument) {
                          onViewSourceDocument(transaction.source_file, transaction.source_page, transaction);
                        }
                      }}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      data-testid={`view-source-${transaction.id}`}
                    >
                      <FileText size={14} />
                      <span className="truncate max-w-[100px] lg:max-w-[150px]" title={transaction.source_file}>
                        {transaction.source_file}
                      </span>
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-sm text-center">
                  {transaction.source_page ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onViewSourceDocument) {
                          onViewSourceDocument(transaction.source_file, transaction.source_page, transaction);
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      data-testid={`view-page-${transaction.id}`}
                    >
                      {transaction.source_page}
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="hidden sm:table-cell px-4 py-3 text-center text-sm">
                  {transaction.ocr_confidence != null ? (
                    <div className="flex items-center justify-center gap-1.5">
                      {/* Confidence bar */}
                      <div className="w-8 lg:w-12 h-2 bg-gray-200 rounded-full overflow-hidden" title={`OCR confidence: ${(transaction.ocr_confidence * 100).toFixed(0)}%`}>
                        <div 
                          className={`h-full rounded-full transition-all ${
                            transaction.ocr_confidence >= 0.9
                              ? 'bg-green-500'
                              : transaction.ocr_confidence >= 0.7
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${transaction.ocr_confidence * 100}%` }}
                        />
                      </div>
                      {/* Percentage badge */}
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          transaction.ocr_confidence >= 0.9
                            ? 'bg-green-100 text-green-800'
                            : transaction.ocr_confidence >= 0.7
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {(transaction.ocr_confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs" title="Native text extraction (no OCR)">—</span>
                  )}
                </td>
                {/* Pattern Suggestion Badge */}
                <td className="hidden sm:table-cell px-2 py-3 text-center">
                  {suggestions[transaction.id] && suggestions[transaction.id].length > 0 ? (
                    <PatternSuggestionBadge
                      suggestion={suggestions[transaction.id][0]}
                      onAccept={handleSuggestionAction}
                      onReject={handleSuggestionAction}
                      compact={true}
                    />
                  ) : (
                    <span className="text-gray-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-2 sm:px-4 py-3 text-center">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.patch(
                          `/api/v1/reconciliation/transactions/${reportId}/${transaction.id}`,
                          { flagged: !transaction.flagged, flag_reason: transaction.flagged ? '' : 'Flagged for review' }
                        );
                        onTransactionsUpdate();
                      } catch (err) {
                        console.error('Error toggling flag:', err);
                      }
                    }}
                    className="text-lg sm:text-xl hover:scale-110 transition-transform touch-manipulation"
                    title={transaction.flagged ? 'Remove flag' : 'Flag for review'}
                  >
                    {transaction.flagged ? '🚩' : '⚑'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedTransactions.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>No transactions match your filters</p>
          </div>
        )}
      </div>
      )}

      {/* Footer with Running Totals - Mobile Responsive */}
      <div className="bg-gray-50 px-2 sm:px-4 py-3 border-t-2 border-gray-300 flex flex-col sm:flex-row justify-around items-center gap-2 sm:gap-0 text-sm font-semibold">
        <div className="flex items-center gap-1">
          <span className="text-gray-600 text-xs sm:text-sm">Total:</span>
          <span className="text-gray-900">{formatCurrency(runningTotal)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-600 text-xs sm:text-sm">Expected:</span>
          <span className="text-gray-900">{formatCurrency(expectedAmount)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-600 text-xs sm:text-sm">Variance:</span>
          <span className={Math.abs(variance) < 1 ? 'text-gray-900' : variance > 0 ? 'text-red-600' : 'text-green-600'}>
            {formatCurrency(variance)}
          </span>
        </div>
      </div>
    </div>
  );
}
